import { WAF_JSAPI_URL } from './wafConfig';

const SCRIPT_ID = 'AwsWAFScript';

// The SDK arrives in two stages, so the tag's own load event is only half way.
const POLL_INTERVAL_MS = 50;
const POLL_CEILING_MS = 10000;
const MAX_POLLS = POLL_CEILING_MS / POLL_INTERVAL_MS;

let loadPromise = null;
let pollTimer = null;

/// True once BOTH halves of the SDK are on `window`.
function sdkReady() {
  return Boolean(window.AwsWafCaptcha && window.AwsWafIntegration);
}

function stopPolling() {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

/// Waits for the second-stage script to define `window.AwsWafIntegration`.
///
/// Polls rather than listening for an event because the second script tag is
/// injected by AWS's code, not ours: there is no handle to attach a listener
/// to. The ceiling stops a page where the second stage never arrives (the
/// token domain blocked by an extension, say) from leaving the caller hanging.
function pollForSdk(resolve, reject) {
  if (sdkReady()) {
    resolve();
    return;
  }

  let polls = 0;
  stopPolling();
  pollTimer = setInterval(() => {
    polls += 1;
    if (sdkReady()) {
      stopPolling();
      resolve();
      return;
    }
    if (polls >= MAX_POLLS) {
      stopPolling();
      loadPromise = null;             // allow a later retry
      reject(
        new Error('The AWS WAF CAPTCHA script did not finish initialising.')
      );
    }
  }, POLL_INTERVAL_MS);
}

/// Loads the AWS WAF JS SDK once, resolving when it is ready to use.
///
/// The SDK loads in TWO stages. `jsapi.js` defines `window.AwsWafCaptcha`
/// synchronously, then injects a SECOND script (`challenge.js`, served from the
/// token domain) and it is that second script which defines
/// `window.AwsWafIntegration`. The tag's `load` event therefore fires while
/// `AwsWafIntegration` is still undefined.
///
/// Resolving on `load` alone was enough for a caller that renders a puzzle
/// first, because the seconds a visitor spends on it cover the second stage.
/// `captchaFetch` calls `AwsWafIntegration.fetch` immediately instead, so on a
/// cold page the first submit threw `Cannot read properties of undefined
/// (reading 'fetch')` into the visitor-facing modal.
///
/// So: resolve only once BOTH globals exist. Already loaded resolves at once;
/// otherwise poll every 50 ms up to a 10 s ceiling, then reject and forget the
/// cached promise so the visitor's next click retries from scratch.
export function loadScript() {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (sdkReady()) {
      resolve();
      return;
    }

    // A tag already in the document may have fired its load event before we got
    // here, so poll for the globals rather than wait for an event that is gone.
    if (document.getElementById(SCRIPT_ID)) {
      pollForSdk(resolve, reject);
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = false;
    script.onload = () => pollForSdk(resolve, reject);
    script.onerror = () => {
      loadPromise = null;               // allow a later retry
      script.remove();                  // ...from a fresh tag, not this dead one
      reject(new Error('Failed to load the AWS WAF CAPTCHA script.'));
    };

    script.src = WAF_JSAPI_URL;
    document.head.appendChild(script);
  });

  return loadPromise;
}

/// Test seam: forget any cached load so each test starts clean.
export function resetScriptLoader() {
  stopPolling();
  loadPromise = null;
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) existing.remove();
}
